import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box, Paper, Typography, IconButton, Tooltip, Collapse
} from '@mui/material';
import {
  PieChart, Pie, Cell, Tooltip as RechartsTooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line
} from 'recharts';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import '../styles/dashboard.css';
import { authToken } from '../globals/Global';
import { getAartiDetails, getAllPurohitProfileList, getAllUsersProfiles, getArticleDetail, getEvents, getMantraDetails, getPoojaCategoryFEData, getYoutubeVideosData } from '../globals/Services';
import PurohitMap from '../components/PurohitMap';
import { useNavigate } from 'react-router-dom';

export default function Dashboard() {
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPurohits, setTotalPurohits] = useState(0);
  const [verifiedPurohits, setVerifiedPurohits] = useState(0);
  const [totalPoojaCategories, setTotalPoojaCategories] = useState(0);
  const [totalSubCategories, setTotalSubCategories] = useState(0);
  const [totalAartis, setTotalAartis] = useState(0);
  const [totalMantras, setTotalMantras] = useState(0);
  const [totalArticles, setTotalArticles] = useState(0);
  const [totalYouTubeVideos, setTotalYouTubeVideos] = useState(0);
  const [totalEvents, setTotalEvents] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);
  const [isMapCollapsed, setIsMapCollapsed] = useState(false);
  const navigate = useNavigate();
  const openMapPage = () => {
    navigate('/PurohitMaps');
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = authToken || localStorage.getItem('sessionToken');

        const usersRes = await axios.get(getAllUsersProfiles, {
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
        });

        const purohitRes = await axios.get(getAllPurohitProfileList, {
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
        });

        const poojaCategoryRes = await axios.get(getPoojaCategoryFEData, {
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
        });

        const aartiRes = await axios.get(getAartiDetails, {
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
        });

        const mantraRes = await axios.get(getMantraDetails, {
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
        });

        const articleRes = await axios.get(getArticleDetail, {
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
        });
        const youtubeRes = await axios.get(getYoutubeVideosData, {
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
        });

        const eventRes = await axios.get(getEvents, {
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
        });

        const users = Array.isArray(usersRes.data?.data)
          ? usersRes.data.data
          : Array.isArray(usersRes.data)
            ? usersRes.data
            : [];

        const purohits = Array.isArray(purohitRes.data?.dataList)
          ? purohitRes.data.dataList
          : Array.isArray(purohitRes.data)
            ? purohitRes.data.dataList
            : [];

        const poojaCategories = Array.isArray(poojaCategoryRes.data?.data)
          ? poojaCategoryRes.data.data
          : Array.isArray(poojaCategoryRes.data)
            ? poojaCategoryRes.data
            : [];

        const aartis = Array.isArray(aartiRes.data?.data)
          ? aartiRes.data.data
          : Array.isArray(aartiRes.data)
            ? aartiRes.data
            : [];

        const mantras = Array.isArray(mantraRes.data?.data)
          ? mantraRes.data.data
          : Array.isArray(mantraRes.data)
            ? mantraRes.data
            : [];

        const articles = Array.isArray(articleRes.data?.data)
        ? articleRes.data.data
        : Array.isArray(articleRes.data)
          ? articleRes.data
          : [];

        const youtubeVideos = Array.isArray(youtubeRes.data?.data)
        ? youtubeRes.data.data
        : Array.isArray(youtubeRes.data)
          ? youtubeRes.data
          : [];

        const events = Array.isArray(eventRes.data?.data)
        ? eventRes.data.data
        : Array.isArray(eventRes.data)
          ? eventRes.data
          : [];

        setTotalUsers(users.length);
        setTotalPurohits(purohits.length);
        setTotalPoojaCategories(poojaCategories.length);
        setTotalAartis(aartis.length);
        setTotalMantras(mantras.length);
        setTotalArticles(articles.length);
        setTotalYouTubeVideos(youtubeVideos.length);  
        setTotalEvents(events.length);

        let verifiedCount_final = purohits.filter(p => p.registrationStatus === 'Completed').length;
        if (verifiedCount_final === 0) {
          const verifiedCount = purohits.filter(p => p.registrationStatus === 'Verified').length;
          const approvedCount = purohits.filter(p => p.registrationStatus === 'Approved').length;
          const activeCount = purohits.filter(p => p.registrationStatus === 'Active').length;
          const hasVerifiedField = purohits.filter(p => p.verified === true || p.isVerified === true).length;
          verifiedCount_final = verifiedCount || approvedCount || activeCount || hasVerifiedField;
        }

        const totalSubs = poojaCategories.reduce((sum, category) => {
          const subCount = Array.isArray(category.poojaSubcategories) ? category.poojaSubcategories.length : 0;
          return sum + subCount;
        }, 0);

        setVerifiedPurohits(verifiedCount_final);
        setTotalSubCategories(totalSubs);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const insights = [
    { 
      title: 'Total Users',
      value: loading ? 0 : totalUsers,
      route: '/UserManagement',
      icon: (
      <img
        src="/users.png"
        alt="Articles"
        style={{ width: 40, height: 40, objectFit: 'contain' }}
      />
    ) 
    },
    { title: 'Total Purohits', 
      value: loading ? 0 : totalPurohits, 
      route: '/PurohitManagement',
      icon: (
      <img
        src="/purohits.png"
        alt="Articles"
        style={{ width: 40, height: 40, objectFit: 'contain' }}
      />
    )  
    },
    { title: 'Verified Purohits', 
      value: loading ? 0 : verifiedPurohits, 
      route: '/PurohitManagement',
      icon: (
      <img
        src="/verified_purohits.png"
        alt="Articles"
        style={{ width: 40, height: 40, objectFit: 'contain' }}
      />
    ) 
   },
    { title: 'Pooja Categories', 
      value: loading ? 0 : totalPoojaCategories, 
      route: '/CategoryManagement',
      icon: (
      <img
        src="/pooja_categories.png"
        alt="Articles"
        style={{ width: 40, height: 40, objectFit: 'contain' }}
      />
    ) 
   },
    { title: 'Pooja Sub categories', 
      value: loading ? 0 : totalSubCategories, 
      route: '/CategoryManagement',
      icon: (
      <img
        src="/pooja_sub_categories.png"
        alt="Articles"
        style={{ width: 40, height: 40, objectFit: 'contain' }}
      />
    ) 
   },
    { title: 'Total Aartis', 
      value: loading ? 0 : totalAartis, 
      route: '/AartiManagement',
      icon: (
      <img
        src="/aartis.png"
        alt="Articles"
        style={{ width: 40, height: 40, objectFit: 'contain' }}
      />
    ) 
   },
    { title: 'Total Mantras', 
      value: loading ? 0 : totalMantras, 
      route: '/MantraList',
      icon: (
      <img
        src="/mantras.png"
        alt="Articles"
        style={{ width: 40, height: 40, objectFit: 'contain' }}
      />
    ) 
   },
    {
    title: 'Total Articles',
    value: loading ? 0 : totalArticles,
    route: '/ArticleManagement',
    icon: (
      <img
        src="/articles.png"
        alt="Articles"
        style={{ width: 40, height: 40, objectFit: 'contain' }}
      />
    )
  },
  {
  title: 'Total YouTube',
  value: loading ? 0 : totalYouTubeVideos,
  route: '/YouTubeManagement',
  icon: (
    <img
      src="/youtube.png"
      alt="YouTube"
      style={{ width: 40, height: 40, objectFit: 'contain' }}
    />
  )
},
{
  title: 'Total Events',
  value: loading ? 0 : totalEvents,
  route: '/EventManagement',
  icon: (
    <img
      src="/event.png"
      alt="Events"
      style={{ width: 40, height: 40, objectFit: 'contain' }}
    />
  )
}
];


  const pieData1 = [
    { name: 'Users', value: totalUsers },
    { name: 'Purohits', value: totalPurohits },
    { name: 'Verified', value: verifiedPurohits }
  ];

  const pieData2 = [
    { name: 'Pooja Categories', value: totalPoojaCategories },
    { name: 'Subcategories', value: totalSubCategories }
  ];

  const pieData3 = [
  { name: 'Aarti', value: totalAartis },
  { name: 'Mantra', value: totalMantras }
  ];

  const COLORS = ['#1976d2', '#9c27b0', '#43a047', '#ff9800', '#00bcd4', '#e91e63'];

  const hasUserData = totalUsers + totalPurohits + verifiedPurohits > 0;

  const toggleMapFullscreen = () => {
  window.open('/PurohitMaps', '_blank'); // Open in new tab
  };

  const toggleMapCollapse = () => {
    setIsMapCollapsed(!isMapCollapsed);
    if (isMapFullscreen) {
      setIsMapFullscreen(false);
    }
  };

  if (error) {
    return (
      <Box className="dashboard-container">
        <Typography variant="h4" gutterBottom>Dashboard Insights</Typography>
        <Typography color="error">Error: {error}</Typography>
      </Box>
    );
  }

  return (
    <Box className="dashboard-container">
      {!isMapFullscreen && (
        <>
          <Typography variant="h4" gutterBottom>Dashboard Insights</Typography>

          <Box className="insight-grid">
            {insights.map((item, idx) => (
              <Paper 
  className={`insight-card card-${idx}`} 
  elevation={3} 
  key={idx}
  onClick={() => item.route && navigate(item.route)}
  sx={{ cursor: item.route ? 'pointer' : 'default' }}>
                <Box className="insight-split-box">
                  <Box className={`left-colored-half color-${idx}`}>
                    <Box className="insight-icon">{item.icon}</Box>
                  </Box>

                  <Box className="right-white-half">
                    <Typography className="insight-title">{item.title}</Typography>
                    <Typography className="insight-value">{item.value}</Typography>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>

          <Box sx={{ mt: 5 }}>
            <Typography variant="h5" gutterBottom>Data Distribution</Typography>

            {hasUserData ? (
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 5, justifyContent: 'center', flexWrap: 'wrap', mb:4}}>
                {/* Pie Chart 1 */}
                <Box sx={{ width: '390px', height: '280px' }}>
                  <Typography variant="h6" align="center">User & Purohit Distribution</Typography>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData1}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label
                      >
                        {pieData1.map((_, index) => (
                          <Cell key={`cell1-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>

                {/* Bar Chart */}
                <Box sx={{ width: '390px', height: '280px' }}>
                  <Typography variant="h6" align="center">Pooja Category Distribution</Typography>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={pieData2} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis allowDecimals={false} />
                      <RechartsTooltip />
                      <Legend />
                      <Bar dataKey="value">
                        {pieData2.map((entry, index) => (
                          <Cell key={`cell-bar2-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>

                {/* Line Chart */}
                <Box sx={{ width: '390px', height: '280px', mt: 4 }}>
                  <Typography variant="h6" align="center">Aarti & Mantra</Typography>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={pieData3} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis allowDecimals={false} />
                      <RechartsTooltip />
                      <Legend />
                      <Line type="monotone" dataKey="value" stroke={COLORS[0]} strokeWidth={3} activeDot={{ r: 8 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            ) : (
              <Typography align="center" color="textSecondary">
                No user data available to display charts.
              </Typography>
            )}
          </Box>
        </>
      )}

      {/* Purohit Map Section */}
      <Box sx={{ 
        mt: isMapFullscreen ? 0 : 6,
        position: isMapFullscreen ? 'fixed' : 'relative',
        top: isMapFullscreen ? 0 : 'auto',
        left: isMapFullscreen ? 0 : 'auto',
        right: isMapFullscreen ? 0 : 'auto',
        bottom: isMapFullscreen ? 0 : 'auto',
        zIndex: isMapFullscreen ? 1300 : 'auto',
        width: isMapFullscreen ? '100vw' : 'auto',
        height: isMapFullscreen ? '100vh' : 'auto',
      }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 2,
          px: isMapFullscreen ? 2 : 0,
          pt: isMapFullscreen ? 2 : 0,
        }}>
          <Typography 
            variant="h5" 
            sx={{ cursor: 'pointer' }} 
            onClick={openMapPage}
          >
            Purohit Locations
          </Typography>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title={isMapCollapsed ? "Expand Map" : "Collapse Map"}>
              <IconButton 
                onClick={toggleMapCollapse}
                sx={{ 
                  backgroundColor: 'secondary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'secondary.dark',
                  }
                }}
              >
                {isMapCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title={isMapFullscreen ? "Exit Fullscreen" : "View Fullscreen"}>
              <IconButton 
                onClick={toggleMapFullscreen}
                disabled={isMapCollapsed}
                sx={{ 
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  '&:disabled': {
                    backgroundColor: 'grey.300',
                    color: 'grey.500',
                  }
                }}
              >
                {isMapFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Collapse in={!isMapCollapsed}>
          <Paper 
            elevation={3} 
            sx={{ 
              height: isMapFullscreen ? 'calc(100vh - 80px)' : '600px',
              overflow: 'hidden' 
            }}
          >
            <PurohitMap 
              bearerToken={authToken || localStorage.getItem('sessionToken')}
              onError={(err) => console.error('Map error:', err)}
            />
          </Paper>
        </Collapse>
      </Box>
    </Box>
  );
}